/**
 * The application root: session gate, then routes.
 *
 * # The gate is a real check, not a guess
 *
 * The session cookie is `httpOnly`, so this code cannot read it — deliberately, per
 * [ADR-0005](../../../docs/05-decisions/adr-0005-spa-auth-model.md): a token JavaScript can read
 * is a token cross-site scripting can steal, and this platform brokers access into client
 * networks. Whether a session exists is therefore established by asking the server, and a 401 is
 * the ordinary pre-sign-in state rather than an error worth showing anyone.
 *
 * The routes here are only the ones the backend actually serves. Screens for unbuilt features are
 * absent rather than stubbed, because a nav full of empty pages teaches people to stop trusting it.
 */

import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import type { JSX } from 'react';

import { Layout } from './Layout';
import { Overview } from '../pages/Overview';
import { SignIn } from '../pages/SignIn';
import { Captures, Collectors, Telemetry } from '../pages/Estate';
import { Tickets } from '../pages/Tickets';
import { SlaBoard } from '../pages/Sla';
import { Ingestion } from '../pages/Ingestion';
import { Playbooks } from '../pages/Playbooks';
import { AccessRequests } from '../pages/AccessRequests';
import { Session } from '../pages/Session';
import {
  Leaderboard,
  RiskMethodology,
  ShadowAssets,
  Vulnerabilities,
} from '../pages/Vulnerabilities';
import { ConfigurationItems } from '../pages/ConfigurationItems';
import { Campaigns } from '../pages/Campaigns';
import { Audit } from '../pages/Audit';
import { Contracts } from '../pages/Contracts';
import { OpsHealth } from '../pages/OpsHealth';
import { Preferences } from '../pages/Preferences';
import { Administration } from '../pages/Administration';
import { followSystemPreference } from '../shared/theme/theme';
import { api, ApiError, type WhoAmI } from '../shared/api/client';
import '../shared/ui/ui.css';
import '../pages/forms.css';

type Session = { state: 'checking' } | { state: 'anonymous' } | { state: 'in'; principal: WhoAmI };

export function App(): JSX.Element {
  const [session, setSession] = useState<Session>({ state: 'checking' });

  // Follow the OS preference only while the user has not chosen a theme explicitly.
  useEffect(() => followSystemPreference(), []);

  const check = useCallback((): void => {
    void api
      .whoami()
      .then((principal) => { setSession({ state: 'in', principal }); })
      .catch((error: unknown) => {
        // A 401 is the ordinary pre-sign-in state. Anything else is still "not signed in" from
        // this screen's point of view — the readiness indicator in the shell is what reports a
        // backend that is actually broken.
        void (error instanceof ApiError);
        setSession({ state: 'anonymous' });
      });
  }, []);

  useEffect(() => { check(); }, [check]);

  if (session.state === 'checking') {
    return <main className="signin" />;
  }

  if (session.state === 'anonymous') {
    return <SignIn onSignedIn={check} />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Layout principal={session.principal} />}>
          <Route index element={<Overview />} />
          <Route path="tickets" element={<Tickets />} />
          <Route path="sla" element={<SlaBoard />} />
          <Route path="ingestion" element={<Ingestion />} />
          <Route path="playbooks" element={<Playbooks />} />
          <Route path="access-requests" element={<AccessRequests />} />
          {/*
            Keyed by the ACCESS REQUEST, not by a session identifier. A session does not exist
            until this screen asks for one, and a URL naming a session would be a URL somebody
            could bookmark and reload — which would either fail on a spent token or, worse,
            look like it should work.
          */}
          <Route path="access-requests/:requestId/session" element={<Session />} />
          <Route path="contracts" element={<Contracts />} />
          <Route path="ops" element={<OpsHealth />} />
          <Route path="preferences" element={<Preferences />} />
          <Route path="administration" element={<Administration />} />
          <Route path="cis" element={<ConfigurationItems />} />
          <Route path="captures" element={<Captures />} />
          <Route path="telemetry" element={<Telemetry />} />
          <Route path="vulnerabilities" element={<Vulnerabilities />} />
          <Route path="shadow-assets" element={<ShadowAssets />} />
          <Route path="leaderboard" element={<Leaderboard />} />
          <Route path="campaigns" element={<Campaigns />} />
          <Route path="risk-config" element={<RiskMethodology />} />
          <Route path="collectors" element={<Collectors />} />
          <Route path="audit" element={<Audit />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
